import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createPortal } from "react-dom";

import { Check, ChevronDown, Plus, Search } from "lucide-react";

import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Optional secondary line rendered under the label (e.g. RUT). */
  description?: string;
  searchTerms?: string;
}

export interface SearchableSelectGroup {
  label: string;
  options: SearchableSelectOption[];
}

interface SearchableSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  options?: SearchableSelectOption[];
  groups?: SearchableSelectGroup[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** When true, typing a value that isn't in the list offers an "add new" row
   *  that reports the raw text. Used for free-text fields (e.g. Misceláneos)
   *  where the value IS the label and no catalog/master record is involved. */
  allowCreate?: boolean;
  /** Called when the user confirms a brand-new typed value. Falls back to
   *  onValueChange when omitted (for the value === label free-text case). */
  onCreate?: (inputValue: string) => void;
  /** Label for the create row; defaults to `Agregar "<text>"`. */
  createLabel?: (inputValue: string) => string;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  groups,
  placeholder = "Seleccionar...",
  emptyMessage = "No se encontraron resultados",
  disabled = false,
  className,
  allowCreate = false,
  onCreate,
  createLabel = (inputValue) => `Agregar "${inputValue}"`,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Calculate dropdown position based on trigger element
  const updateDropdownPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      // Match the trigger width (min 240) and keep the panel inside the viewport so it
      // never spills past a dialog/edge. A definite width lets option labels truncate.
      const width = Math.min(
        Math.max(rect.width, 240),
        window.innerWidth - 16
      );
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      setDropdownPosition({
        top: rect.bottom + 2,
        left,
        width,
      });
    }
  }, []);

  // Get all options (flat list or from groups)
  const allOptions = useMemo(() => {
    if (options) return options;
    if (groups) return groups.flatMap((g) => g.options);
    return [];
  }, [options, groups]);

  // Find the selected option's label
  const selectedOption = useMemo(() => {
    return allOptions.find((opt) => opt.value === value);
  }, [allOptions, value]);

  // Filter options based on search
  const filterOptions = (opts: SearchableSelectOption[]) => {
    if (!search) return opts;
    const searchLower = search.toLowerCase();
    return opts.filter((opt) => {
      const searchText = opt.searchTerms || opt.label;
      return searchText.toLowerCase().includes(searchLower);
    });
  };

  const filteredOptions = useMemo(
    () => filterOptions(allOptions),
    [allOptions, search]
  );

  const filteredGroups = useMemo(() => {
    if (!groups) return null;
    return groups
      .map((group) => ({
        ...group,
        options: filterOptions(group.options),
      }))
      .filter((group) => group.options.length > 0);
  }, [groups, search]);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking inside the container or the dropdown portal
      if (containerRef.current?.contains(target)) return;
      if (target.closest?.(".searchable-select-dropdown")) return;
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update position when opening and close on page scroll (not dropdown scroll)
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();

      const handleScroll = (e: Event) => {
        // Don't close if scrolling inside the dropdown
        const target = e.target as HTMLElement;
        if (target.closest?.(".searchable-select-dropdown")) return;
        setIsOpen(false);
      };
      window.addEventListener("scroll", handleScroll, true);
      return () => window.removeEventListener("scroll", handleScroll, true);
    }
  }, [isOpen, updateDropdownPosition]);

  const handleSelect = (selectedValue: string) => {
    onValueChange(selectedValue);
    setIsOpen(false);
    setSearch("");
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      if (inputRef.current) {
        inputRef.current.select();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const hasResults = groups
    ? (filteredGroups?.length ?? 0) > 0
    : filteredOptions.length > 0;

  // Creatable: offer an "add new" row when the typed text isn't already an
  // option (case-insensitive). Reports the raw text, never a catalog id.
  const trimmedSearch = search.trim();
  const createOptionVisible =
    allowCreate &&
    trimmedSearch.length > 0 &&
    !allOptions.some(
      (opt) => opt.label.toLowerCase() === trimmedSearch.toLowerCase()
    );

  const handleCreate = () => {
    (onCreate ?? onValueChange)(trimmedSearch);
    setIsOpen(false);
    setSearch("");
  };

  const renderOption = (option: SearchableSelectOption) => (
    <div
      key={option.value}
      className={cn(
        "flex cursor-pointer items-start px-3 py-2 text-sm hover:bg-accent",
        value === option.value && "bg-accent"
      )}
      onClick={() => handleSelect(option.value)}
    >
      <Check
        className={cn(
          "mr-2 mt-0.5 h-4 w-4 flex-shrink-0",
          value === option.value ? "opacity-100" : "opacity-0"
        )}
      />
      <div className="min-w-0">
        <div className="truncate">{option.label}</div>
        {option.description && (
          <div className="truncate text-xs text-muted-foreground">
            {option.description}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      <div className="relative" ref={triggerRef}>
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Search className="h-4 w-4 text-muted-foreground" />
        </div>
        <Input
          ref={inputRef}
          value={
            isOpen
              ? search
              : (selectedOption?.label ?? (allowCreate ? value ?? "" : "")) ||
                ""
          }
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          className="h-9 pl-9 pr-8"
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </div>
      </div>

      {isOpen &&
        createPortal(
          <div
            className="searchable-select-dropdown fixed z-[9999] origin-top rounded-md border bg-popover shadow-lg duration-200 ease-out animate-in fade-in-0 zoom-in-95 slide-in-from-top-2"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              maxWidth: "calc(100vw - 16px)",
              pointerEvents: "auto",
            }}
          >
            <div
              className="max-h-60 overflow-y-auto py-1"
              style={{
                maxHeight: "240px",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {!hasResults && !createOptionVisible ? (
                <div className="px-4 py-3 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </div>
              ) : (
                <>
                  {hasResults &&
                    (groups && filteredGroups
                      ? filteredGroups.map((group) => (
                          <div key={group.label}>
                            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground">
                              {group.label}
                            </div>
                            {group.options.map(renderOption)}
                          </div>
                        ))
                      : filteredOptions.map(renderOption))}
                  {createOptionVisible && (
                    <div
                      className="flex cursor-pointer items-center px-3 py-2 text-sm text-primary hover:bg-accent"
                      onClick={handleCreate}
                    >
                      <Plus className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span className="truncate">
                        {createLabel(trimmedSearch)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
